import * as Tone from "tone";

/* ------------------------------------------------------------------
   Roman-numeral chord parser and builder
   ------------------------------------------------------------------ */

/* eslint-disable no-unused-vars, @typescript-eslint/no-unused-vars */
export enum Quality {
  MAJ = "maj",
  MIN = "min",
  DIM = "dim",
  AUG = "aug",
  SUS2 = "sus2",
  SUS4 = "sus4",
  POWER5 = "5",
}

export enum SeventhQuality {
  MINOR = "min7", // flat 7th (10 semitones) - default for dominant chords
  MAJOR = "maj7", // major 7th (11 semitones)
  DIMINISHED = "dim7", // diminished 7th (9 semitones)
}
/* eslint-enable no-unused-vars, @typescript-eslint/no-unused-vars */

export interface ChordToken {
  degree: number; // 1-7 after accidentals
  quality: Quality; // chord quality
  accidental: number; // –1 for flat, +1 for sharp, 0 for natural
  hasSeventh: boolean; // true for vii°7 etc.
  seventhQuality?: SeventhQuality; // quality of the 7th when present
  extraOffsets: number[]; // additional chord-tones in semitones above root
  bassDegree?: number; // for slash chords, optional
  bassAccidental?: number; // accidental for bass note in slash chords
}

/* ------------------------------------------------------------------
   TABLES
   ------------------------------------------------------------------ */

export const DEGREE_TO_SEMITONE_MAJOR = [0, 2, 4, 5, 7, 9, 11]; // 1-7
export const DEGREE_TO_SEMITONE_NAT_MINOR = [0, 2, 3, 5, 7, 8, 10];
export const DEGREE_TO_SEMITONE_HARM_MINOR = [0, 2, 3, 5, 7, 8, 11];

export const QUALITY_TO_OFFSETS: Record<Quality, number[]> = {
  [Quality.MAJ]: [0, 4, 7],
  [Quality.MIN]: [0, 3, 7],
  [Quality.DIM]: [0, 3, 6],
  [Quality.AUG]: [0, 4, 8],
  [Quality.SUS2]: [0, 2, 7],
  [Quality.SUS4]: [0, 5, 7],
  [Quality.POWER5]: [0, 7],
};

/**
 * chordToMidi
 * Convert a ChordToken to absolute MIDI pitches given
 * - rootNote: MIDI number of scale degree 1 (e.g. 60 for C)
 * - minor: true for minor keys
 * - useHarmonicMinor: raise 7th when true
 */
export function chordToMidi(
  token: ChordToken,
  rootNote: number,
  minor = false,
  useHarmonicMinor = true
): number[] {
  if (
    minor &&
    token.accidental === -1 &&
    (token.degree === 3 ||
      token.degree === 6 ||
      (!useHarmonicMinor && token.degree === 7))
  ) {
    token.accidental = 0; // flat is redundant in minor
  }
  const scale = selectScale(minor, useHarmonicMinor);
  const rootDegree = wrapDegree(token.degree);
  const rootSemitone = rootNote + scale[rootDegree - 1] + token.accidental;

  const chord: number[] = QUALITY_TO_OFFSETS[token.quality].map(
    (o) => rootSemitone + o
  );

  if (token.hasSeventh) {
    let seventhOffset = 10; // minor 7th by default
    if (token.seventhQuality === SeventhQuality.MAJOR) {
      seventhOffset = 11; // major 7th
    } else if (token.seventhQuality === SeventhQuality.DIMINISHED) {
      seventhOffset = 9; // diminished 7th
    }
    chord.push(rootSemitone + seventhOffset);
  }
  if (token.extraOffsets.length) {
    chord.push(...token.extraOffsets.map((semi) => rootSemitone + semi));
  }
  chord.sort((a, b) => a - b);

  if (token.bassDegree) {
    const bassDegree = wrapDegree(token.bassDegree);
    const bassAccidental = token.bassAccidental ?? 0;
    const bassSemitone = rootNote + scale[bassDegree - 1] + bassAccidental;
    chord.unshift(bassSemitone); // pedal or slash bass
  }

  return chord;
}

/* ------------------------------------------------------------------
   INTERNAL HELPERS
   ------------------------------------------------------------------ */

/**
 * parseChordToken
 * Recognise one Roman-numeral symbol and turn it into a ChordToken.
 */
export function parseChordToken(token: string): ChordToken {
  const result: ChordToken = {
    degree: 1,
    quality: Quality.MAJ,
    accidental: 0,
    hasSeventh: false,
    extraOffsets: [],
  };

  // Look for slash bass first: V/V, iii/7, ii7/I, v7/♭7, etc.
  const [body, bass] = token.split("/");
  if (bass) {
    // Check if bass is numeric (e.g., /7, /9) or roman (e.g., /V, /I)
    const numericBassMatch = bass.match(/^([#♯b♭]?)(\d+)$/);
    if (numericBassMatch) {
      // Numeric bass like /7 or /♭7
      result.bassDegree = parseInt(numericBassMatch[2], 10);
      result.bassAccidental = accidentalSymbol(numericBassMatch[1]);
    } else {
      // Roman numeral bass like /V or /I
      result.bassDegree = romanNumeralToDegree(stripAccidentals(bass));
      result.bassAccidental = accidentalSymbol(bass);
    }
  }

  // Accidentals in main body (but not those that are part of altered extensions like 7#9)
  // We need to extract accidentals that come BEFORE the roman numeral
  const accidentalMatch = body.match(/^([#♯b♭]+)/);
  if (accidentalMatch) {
    result.accidental += accidentalSymbol(accidentalMatch[1]);
  }

  // Numeral
  const numeralMatch = body.match(/[IViv]+/);
  if (!numeralMatch) throw new Error(`Bad numeral: ${token}`);
  result.degree = romanNumeralToDegree(numeralMatch[0]);

  // Case gives default quality
  result.quality =
    numeralMatch[0] === numeralMatch[0].toUpperCase()
      ? Quality.MAJ
      : Quality.MIN;

  // Suffixes - check for dim7 suffix first before checking for ° or dim
  if (body.toLowerCase().includes("dim7")) {
    result.quality = Quality.DIM;
    result.hasSeventh = true;
    result.seventhQuality = SeventhQuality.DIMINISHED;
  } else if (body.includes("°")) {
    result.quality = Quality.DIM;
  } else if (body.includes("+")) {
    result.quality = Quality.AUG;
  } else if (body.toLowerCase().includes("sus4")) {
    result.quality = Quality.SUS4;
  } else if (body.toLowerCase().includes("sus2")) {
    result.quality = Quality.SUS2;
  }

  // Check for power chord (5) - must be standalone "5" at the end
  // Not part of add5, #5, b5, etc.
  // Pattern: Roman numeral followed immediately by "5" at the end, with no "add" or accidentals before it
  const powerChordMatch = body.match(/[IViv]+5$/);
  if (
    powerChordMatch &&
    !body.includes("sus") &&
    !body.includes("add") &&
    !body.match(/[#♯b♭]5/)
  ) {
    result.quality = Quality.POWER5;
    // Don't process this "5" as an extension later
    // We can return early or set a flag, but since we're checking this before extensions, we're good
  }

  // Check for 6th chords (not 7ths, but adding the 6th scale degree)
  // This needs to be checked before extensions to avoid matching as 6th extension
  const sixthMatch = body.match(/(?<!add\s*)(?<![79])6(?![0-9])/);
  if (sixthMatch && !result.hasSeventh && result.quality !== Quality.POWER5) {
    // Add 6th (9 semitones) as an extra offset
    result.extraOffsets.push(9);
  }

  // Check for altered extensions first (7#9, 7b9, etc.)
  const alteredExtMatch = body.match(/7([#♯b♭])(\d+)/);
  if (alteredExtMatch && !result.hasSeventh) {
    // Has a 7th with altered extension like 7#9 or 7b9
    result.hasSeventh = true;

    // Determine 7th quality
    if (body.includes("maj7")) {
      result.seventhQuality = SeventhQuality.MAJOR;
    } else if (result.quality === Quality.DIM) {
      result.seventhQuality = SeventhQuality.DIMINISHED;
    } else {
      result.seventhQuality = SeventhQuality.MINOR;
    }

    // Parse the altered extension
    const alteration = alteredExtMatch[1];
    const extension = parseInt(alteredExtMatch[2], 10);
    let extensionSemitones = intervalToSemitone(extension);

    if (alteration === "#" || alteration === "♯") {
      extensionSemitones += 1;
    } else if (alteration === "b" || alteration === "♭") {
      extensionSemitones -= 1;
    }

    result.extraOffsets.push(extensionSemitones);
  }

  // Check for extended chords (7, 9, 11, 13) - these all imply a 7th
  // But exclude "add" chords which are handled separately
  // Also skip if we already handled altered extensions or dim7 or power chords
  if (
    !result.hasSeventh &&
    !body.toLowerCase().includes("dim7") &&
    result.quality !== Quality.POWER5
  ) {
    const extensionMatch = body.match(/(?<!add\s*)(\d+)$/);
    if (extensionMatch) {
      const extension = parseInt(extensionMatch[1], 10);

      // Extensions 7, 9, 11, 13 all include a 7th
      if (extension >= 7 && [7, 9, 11, 13].includes(extension)) {
        result.hasSeventh = true;

        // Determine 7th quality based on context
        if (
          body.includes("maj7") ||
          body.includes("maj9") ||
          body.includes("maj11") ||
          body.includes("maj13")
        ) {
          result.seventhQuality = SeventhQuality.MAJOR;
        } else if (
          body.includes("°7") ||
          body.includes("°9") ||
          body.includes("°11") ||
          body.includes("°13") ||
          (result.quality === Quality.DIM && body.includes("7"))
        ) {
          result.seventhQuality = SeventhQuality.DIMINISHED;
        } else {
          // Default to minor 7th (dominant 7th) for extensions
          result.seventhQuality = SeventhQuality.MINOR;
        }

        // Add the extension tone if it's not just a 7th
        if (extension > 7) {
          const extensionSemitones = intervalToSemitone(extension);
          result.extraOffsets.push(extensionSemitones);
        }
      }
    }
  }

  // Added tones (simple form: add#5, add9 etc.)
  const addMatch = body.match(/add\s*([#♯b♭]?)(\d+)/i);
  if (addMatch) {
    const interval = parseInt(addMatch[2], 10);
    let semis = intervalToSemitone(interval);
    if (addMatch[1] === "#" || addMatch[1] === "♯") semis += 1;
    if (addMatch[1] === "b" || addMatch[1] === "♭") semis -= 1;
    result.extraOffsets.push(semis);
  }

  return result;
}

/* degree 1-7 from Roman string */
function romanNumeralToDegree(numeral: string): number {
  const map: Record<string, number> = {
    I: 1,
    II: 2,
    III: 3,
    IV: 4,
    V: 5,
    VI: 6,
    VII: 7,
    i: 1,
    ii: 2,
    iii: 3,
    iv: 4,
    v: 5,
    vi: 6,
    vii: 7,
  };
  return map[numeral] ?? 1;
}

/* Select scale array */
function selectScale(minor: boolean, harmonic: boolean): number[] {
  if (!minor) return DEGREE_TO_SEMITONE_MAJOR;
  return harmonic
    ? DEGREE_TO_SEMITONE_HARM_MINOR
    : DEGREE_TO_SEMITONE_NAT_MINOR;
}

/* Accidentals: returns –1, 0 or +1 */
function accidentalSymbol(text: string): number {
  if (text.includes("b") || text.includes("♭")) return -1;
  if (text.includes("#") || text.includes("♯")) return 1;
  return 0;
}

/* Remove accidentals from token */
function stripAccidentals(text: string): string {
  return text.replace(/[b#♭♯]/g, "");
}

/* Convert 9, 11, 13, etc. to semitone offsets */
function intervalToSemitone(interval: number): number {
  const basic = [0, 2, 4, 5, 7, 9, 11]; // 1-7
  const degree = ((interval - 1) % 7) + 1;
  const octave = Math.floor((interval - 1) / 7);
  return basic[degree - 1] + octave * 12;
}

/* Keep degree within 1-7 */
function wrapDegree(d: number): number {
  return ((((d - 1) % 7) + 7) % 7) + 1;
}

export function midiToNote(midi: number): string {
  return Tone.Frequency(midi, "midi").toNote(); // e.g. 60 -> "C4"
}
